import {
  InfiniteData,
  infiniteQueryOptions,
  queryOptions,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
} from '@tanstack/react-query';
import isNil from 'lodash/isNil';
import {CONTENT_QUERY_KEY_MAP} from '../api/content/constant';
import {getContentAbout} from '../api/content/getContentAbout';
import {getContentNotice} from '../api/content/getContentNotice';
import {getContentOverview} from '../api/content/getContentOverview';
import {getContentProductCursorIndex} from '../api/content/getContentProductCursorIndex';
import {getContentProductList} from '../api/content/getContentProductList';
import {getContentRecommend} from '../api/content/getContentRecommend';
import {getContentSeriesSimple} from '../api/content/getContentSeriesSimple';
import {getContentSetList} from '../api/content/getContentSetList';
import {getContentSetOverview} from '../api/content/getContentSetOverview';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createContentHomeAbout} from '../graphql/factory/ContentHomeAbout';
import {createContentHomeNotice} from '../graphql/factory/ContentHomeNotice';
import {createContentHomeOverview} from '../graphql/factory/ContentHomeOverview';
import {createContentHomeRecommend} from '../graphql/factory/ContentHomeRecommend';
import {createContentSetList} from '../graphql/factory/ContentSetList';
import {createContentSetOverview} from '../graphql/factory/ContentSetOverview';
import {createSimpleSeriesSingleInfo} from '../graphql/factory/SimpleSeriesSingleInfo';
import {createSingleListConnection} from '../graphql/factory/SingleListConnection';
import type {
  ContentHomeAboutQuery,
  ContentHomeAboutQueryVariables,
  ContentHomeInfoQuery,
  ContentHomeInfoQueryVariables,
  ContentHomeNoticeQuery,
  ContentHomeNoticeQueryVariables,
  ContentHomeOverviewQuery,
  ContentHomeOverviewQueryVariables,
  ContentHomeProductListQuery,
  ContentHomeProductListQueryVariables,
  ContentHomeRecommendQuery,
  ContentHomeRecommendQueryVariables,
  ContentProductCursorIndexQuery,
  ContentProductCursorIndexQueryVariables,
  ContentSetListQuery,
  ContentSetListQueryVariables,
  ContentSetOverviewQuery,
  ContentSetOverviewQueryVariables,
  SimpleSeriesSingleInfoQuery,
  SimpleSeriesSingleInfoQueryVariables,
} from '../graphql/generated/types';
import {getContentHomeProductListParam} from './util/getContentHomeProductListParam';

/**
 * Content 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const contentFactory = {
  /**
   * 콘텐츠 홈 소개 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - Factory 함수에 userInfoToken 전달하지만, 실제로는 Factory 내부에서 사용되지 않음
   * - 클라이언트 환경에서는 null 전달
   */
  contentHomeAbout: (
    variables: ContentHomeAboutQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentHomeAboutQuery,
        PagewebError,
        ContentHomeAboutQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeAbout(variables),
      queryFn: async () => {
        const response = await getContentAbout(variables, fetcherOptions);

        return {
          contentHomeAbout: createContentHomeAbout(null, response.result),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 정보 조회 (about + recommend 동시 조회)
   *
   * ⚠️ Resolver 로직:
   * - 두 개의 API를 동시에 호출 (getContentHomeAbout, getContentHomeRecommend)
   * - 두 개의 Factory 함수 사용 (createContentHomeAbout, createContentHomeRecommend)
   * - 반환값: { about, recommend } 객체 구조
   */
  contentHomeInfo: (
    variables: ContentHomeInfoQueryVariables,
    options?: Omit<
      UseQueryOptions<ContentHomeInfoQuery, PagewebError, ContentHomeInfoQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeInfo(variables),
      queryFn: async () => {
        // 두 API 병렬 호출
        const [aboutResponse, recommendResponse] = await Promise.all([
          getContentAbout(variables, fetcherOptions),
          getContentRecommend(variables, fetcherOptions),
        ]);

        return {
          contentHomeInfo: {
            about: createContentHomeAbout(null, aboutResponse.result),
            recommend: createContentHomeRecommend(null, {
              series_id: variables.seriesId,
              vo: recommendResponse.result,
            }),
          },
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 공지사항 조회
   *
   * ⚠️ Resolver 로직:
   * - Factory 함수에 series_id와 contentHomeNoticeVo 전달
   */
  contentHomeNotice: (
    variables: ContentHomeNoticeQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentHomeNoticeQuery,
        PagewebError,
        ContentHomeNoticeQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeNotice(variables),
      queryFn: async () => {
        const response = await getContentNotice(
          variables.contentHomeNoticeInput,
          fetcherOptions
        );

        return {
          contentHomeNotice: createContentHomeNotice({
            series_id: variables.contentHomeNoticeInput.seriesId,
            contentHomeNoticeVo: response.result,
          }),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 공지사항 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   */
  infiniteContentHomeNotice: (
    variables: ContentHomeNoticeQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        ContentHomeNoticeQuery,
        PagewebError,
        InfiniteData<ContentHomeNoticeQuery, ContentHomeNoticeQueryVariables>,
        ContentHomeNoticeQuery,
        ReturnType<typeof CONTENT_QUERY_KEY_MAP.infiniteContentHomeNotice>,
        ContentHomeNoticeQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.infiniteContentHomeNotice(variables),
      queryFn: async ({pageParam}) => {
        const page = pageParam?.contentHomeNoticeInput?.page ?? 0;

        const response = await getContentNotice(
          {
            seriesId: variables.contentHomeNoticeInput.seriesId,
            page,
          },
          fetcherOptions
        );

        return {
          contentHomeNotice: createContentHomeNotice({
            series_id: variables.contentHomeNoticeInput.seriesId,
            contentHomeNoticeVo: response.result,
          }),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 개요 정보 조회
   */
  contentHomeOverview: (
    variables: ContentHomeOverviewQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentHomeOverviewQuery,
        PagewebError,
        ContentHomeOverviewQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeOverview(variables),
      queryFn: async () => {
        const response = await getContentOverview(variables, fetcherOptions);

        return {
          contentHomeOverview: createContentHomeOverview(response.result),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 작품 목록 조회
   *
   * ⚠️ Resolver 로직:
   * - isMobile: requestContext.requestInfo.display.isMobile 사용
   * - cursor_direction: after/before/sortType에 따라 NEXT/PREV/INIT 결정
   * - PC INIT인 경우 ANCHOR로 변경
   * - window_size: PC INIT은 6, 그 외는 first/last (default: 25)
   */
  contentHomeProductList: (
    variables: ContentHomeProductListQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentHomeProductListQuery,
        PagewebError,
        ContentHomeProductListQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeProductList(variables),
      queryFn: async () => {
        const contentProductListParam =
          getContentHomeProductListParam(variables);

        const response = await getContentProductList(
          contentProductListParam,
          fetcherOptions
        );

        return {
          contentHomeProductList: createSingleListConnection({
            params: contentProductListParam,
            result: response.result,
          }),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 작품 목록 조회 (Infinite)
   *
   * ⚠️ options는 required (컴포넌트에서 initialPageParam, getNextPageParam 주입 필요)
   */
  infiniteContentHomeProductList: (
    variables: ContentHomeProductListQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        ContentHomeProductListQuery,
        PagewebError,
        InfiniteData<
          ContentHomeProductListQuery,
          ContentHomeProductListQueryVariables
        >,
        ContentHomeProductListQuery,
        ReturnType<typeof CONTENT_QUERY_KEY_MAP.infiniteContentHomeProductList>,
        ContentHomeProductListQueryVariables
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.infiniteContentHomeProductList(variables),
      queryFn: async ({pageParam}) => {
        const contentProductListParam =
          getContentHomeProductListParam(pageParam);

        const response = await getContentProductList(
          contentProductListParam,
          fetcherOptions
        );

        return {
          contentHomeProductList: createSingleListConnection({
            params: contentProductListParam,
            result: response.result,
          }),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 홈 추천 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - Factory 함수에 series_id와 vo 전달
   */
  contentHomeRecommend: (
    variables: ContentHomeRecommendQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentHomeRecommendQuery,
        PagewebError,
        ContentHomeRecommendQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentHomeRecommend(variables),
      queryFn: async () => {
        const response = await getContentRecommend(variables, fetcherOptions);

        return {
          contentHomeRecommend: createContentHomeRecommend(null, {
            series_id: variables.seriesId,
            vo: response.result,
          }),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 작품 커서 인덱스 조회
   *
   * ⚠️ Resolver 로직:
   * - response.result.last_cursor_index가 nil이거나 -1이면 undefined 반환
   */
  contentProductCursorIndex: (
    variables: ContentProductCursorIndexQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentProductCursorIndexQuery,
        PagewebError,
        ContentProductCursorIndexQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentProductCursorIndex(variables),
      queryFn: async () => {
        const response = await getContentProductCursorIndex(
          {
            seriesId: variables.seriesId,
            sortType: variables.sortType ?? undefined,
          },
          fetcherOptions
        );

        if (
          isNil(response?.result?.last_cursor_index) ||
          response.result.last_cursor_index === -1
        ) {
          return {contentProductCursorIndex: undefined};
        }

        return {
          contentProductCursorIndex: response.result.last_cursor_index,
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 세트 목록 조회
   */
  contentSetList: (
    variables: ContentSetListQueryVariables,
    options?: Omit<
      UseQueryOptions<ContentSetListQuery, PagewebError, ContentSetListQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentSetList(variables),
      queryFn: async () => {
        const response = await getContentSetList(variables, fetcherOptions);

        return {
          contentSetList: createContentSetList(response.result),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 세트 개요 조회
   */
  contentSetOverview: (
    variables: ContentSetOverviewQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentSetOverviewQuery,
        PagewebError,
        ContentSetOverviewQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.contentSetOverview(variables),
      queryFn: async () => {
        const response = await getContentSetOverview(variables, fetcherOptions);

        return {
          contentSetOverview: createContentSetOverview(null, response.result),
        };
      },
      ...options,
    }),

  /**
   * 콘텐츠 단일 회차 기본 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - API 응답의 result가 없으면 에러 발생
   */
  simpleSeriesSingleInfo: (
    variables: SimpleSeriesSingleInfoQueryVariables,
    options?: Omit<
      UseQueryOptions<
        SimpleSeriesSingleInfoQuery,
        PagewebError,
        SimpleSeriesSingleInfoQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: CONTENT_QUERY_KEY_MAP.simpleSeriesSingleInfo(variables),
      queryFn: async () => {
        const response = await getContentSeriesSimple(
          {
            seriesId: variables.seriesId,
            productId: variables.productId ?? undefined,
          },
          fetcherOptions
        );

        if (!response.result) {
          throw createSimpleApiResponseError('결과를 받아오지 못했습니다');
        }

        return {
          simpleSeriesSingleInfo: createSimpleSeriesSingleInfo(response.result),
        };
      },
      ...options,
    }),
} as const;
